import {
  EmployeeDto,
  EmployeePositionDto,
  EmployeeDepartmentDto,
  EmployeeTypeCategoryID,
} from "Api/Api";
import { EmployeeEditForm } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditForm";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch, useHistory } from "react-router-dom";
import {
  getHumanResourcesEmployeeDetailAsync,
  resetHumanResourcesEmployeeDetail,
  toggleEmployeeWasSaved,
  saveHumanResourcesEmployeeAsync,
} from "State/HumanResources/HumanResourcesReducer";
import { RootStateType } from "State/Store";
import { Resources, useResource } from "Translations/Resources";
import styled from "styled-components";
import { setDisableRedirect } from "State/App/AppReducer";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";
import { EmployeesSideMenu } from "Components/HumanResources/Employees/EmployeesSideMenu";
import { isNoU } from "Utils/ObjectUtils";
import { getEmployeeBaseUrl } from "Components/HumanResources/Employees/EmployeeDetail/EmployeeDetailPage";

type RouteProps = {
  id: string;
};

type Props = {
  isCreate: boolean;
  employeeTypeCategoryID: EmployeeTypeCategoryID;
};

const getNewEmployee = (employeeTypeCategoryID: EmployeeTypeCategoryID) => {
  return {
    positions: [] as EmployeePositionDto[],
    departments: [] as EmployeeDepartmentDto[],
    employeeTypeCategoryID: employeeTypeCategoryID,
  } as EmployeeDto;
};

const StyledLayout = styled.div`
  ${props => props.theme.breakpoints.down("xs")} {
    h3 {
      padding: ${props => props.theme.spacing(0, 1)};
    }
  }

  .box-header-buttons {
    display: flex;

    .edit {
      margin-right: 10px;
    }
  }
`;

export const EmployeeEditPage: React.FunctionComponent<Props> = props => {
  const { isCreate } = props;
  const { t } = useResource();
  const dispatch = useDispatch();
  const history = useHistory();
  const { params } = useRouteMatch<RouteProps>();
  const employeeID = params.id as any;
  const [employee, setEmployee] = React.useState<EmployeeDto | null>(null);

  const {
    employeeDetail,
    isEmployeeDetailLoading: isLoading,
    wasEmployeeSaved,
  } = useSelector((e: RootStateType) => e.humanResources);

  React.useEffect(() => {
    dispatch(resetHumanResourcesEmployeeDetail());
    if (!isCreate) {
      dispatch(getHumanResourcesEmployeeDetailAsync.request(employeeID));
    }
    return () => {
      dispatch(resetHumanResourcesEmployeeDetail());
    };
  }, [dispatch, employeeID, isCreate]);

  React.useEffect(() => {
    setEmployee(
      isCreate ? getNewEmployee(props.employeeTypeCategoryID) : employeeDetail,
    );

    if (wasEmployeeSaved && employeeDetail?.employeeID) {
      dispatch(setDisableRedirect(false));
      dispatch(toggleEmployeeWasSaved());
      history.replace(
        `/${getEmployeeBaseUrl(t)}/${t(Resources.Routes.EmployeeEdit)}/${
          employeeDetail.employeeID
        }`,
      );
    }
    // eslint-disable-next-line
  }, [
    employeeDetail,
    isCreate,
    props.employeeTypeCategoryID,
    wasEmployeeSaved,
  ]);

  const handleOnSave = (employeeToSave: EmployeeDto) => {
    dispatch(
      saveHumanResourcesEmployeeAsync.request({
        employee: {
          ...employee,
          ...employeeToSave,
        },
      }),
    );
    dispatch(setDisableRedirect(false));
  };

  const getPageTitle = () => {
    return isCreate
      ? t(Resources.HumanResources.EmployeeEdit.NewEmployee)
      : `${employeeDetail?.firstName ?? ""} ${employeeDetail?.lastName ?? ""}`;
  };

  const getTitle = () => {
    return isCreate
      ? t(Resources.HumanResources.EmployeeEdit.NewEmployee)
      : t(Resources.HumanResources.EmployeeEdit.Breadcrumb.Title);
  };

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    return [
      {
        name: "Kontakty",
        url: getEmployeeBaseUrl(t),
      },
      {
        name: getTitle(),
      },
    ];
  };

  return (
    <StyledLayout>
      <AuthenticatedLayout
        title={getPageTitle()}
        hasSideMenu={!isNoU(employeeID)}
        breadcrumbItems={getBreadcrumbItems()}
      >
        <EmployeesSideMenu
          employeeTypeCategoryID={props.employeeTypeCategoryID}
          employeeID={employeeID}
        />

        <div className={"container-small"}>
          <div className={"box"}>
            <div className={"box-header"}>
              {!isLoading && employee ? (
                getTitle()
              ) : (
                <PrimarySkeleton height={30} />
              )}
            </div>
            <div className={"box-content"}>
              {!isLoading && employee ? (
                <EmployeeEditForm
                  employee={employee}
                  isLoading={isLoading}
                  onSave={handleOnSave}
                />
              ) : (
                <>
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                </>
              )}
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    </StyledLayout>
  );
};
